/**
 * 成绩体系 · 按学科(博颂 2026-09-28)。
 * 左:学科管理里的学科;右:该学科的评价标准(评估项 = 名称 + 大类 + 权重)和各年级学分。
 * 评估项的大类决定它出现在哪里:考试类 → 新建考试的「类型」;作业类 → 新建作业;
 * 课堂类 → 课堂诊断 / 课堂记录;手工录入 → 学期成绩里手工录分。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../api/school";
import { DEFAULT_LEVELS, SCALE_TYPES, itemLevels, itemScale, type ScaleLevel, type ScaleType } from "../../lib/scale";

type Category = "exam" | "homework" | "classroom" | "manual";
const CATEGORIES: Category[] = ["exam", "homework", "classroom", "manual"];
const GRADES = ["G7", "G8", "G9", "G10", "G11", "G12"];
const CAT_CLS: Record<Category, string> = {
  exam: "bg-rose-50 text-rose-700 border-rose-200",
  homework: "bg-amber-50 text-amber-700 border-amber-200",
  classroom: "bg-emerald-50 text-emerald-700 border-emerald-200",
  manual: "bg-slate-100 text-slate-600 border-slate-200",
};

interface Item { key: string; label: string; weight: number; category: Category; source?: string; scale?: ScaleType; levels?: ScaleLevel[] }
interface GradeRule { credits: number | null; gpa_enabled: boolean; graded: boolean }
interface SubjectCfg {
  slug: string;
  name: string;
  scheme: { id: string; name: string; items: Item[] } | null;
  grades: Record<string, GradeRule>;
  course_overrides: { course: string; scheme_name: string | null }[];
}

const TEMPLATE: Item[] = [
  { key: "", label: "期中考试", weight: 30, category: "exam" },
  { key: "", label: "期末考试", weight: 30, category: "exam" },
  { key: "", label: "日常测验", weight: 15, category: "exam" },
  { key: "", label: "家庭作业", weight: 15, category: "homework" },
  { key: "", label: "课堂表现", weight: 10, category: "classroom" },
];

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, { method, headers: { ...authHeaders(), ...(body !== undefined ? { "Content-Type": "application/json" } : {}) }, body: body !== undefined ? JSON.stringify(body) : undefined });
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(typeof b.detail === "string" ? b.detail : `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

export function SubjectGradingSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ["gpa-subject-config"], queryFn: () => req<SubjectCfg[]>("GET", "/api/school/gpa/config/subjects") });
  const [slug, setSlug] = useState<string | null>(null);
  const list = cfg.data ?? [];
  const current = list.find((s) => s.slug === slug) ?? list[0] ?? null;
  const save = useMutation({
    mutationFn: (p: { slug: string; items: Item[]; grades: Record<string, GradeRule> }) =>
      req<SubjectCfg[]>("PUT", `/api/school/gpa/config/subjects/${p.slug}`, { items: p.items, grades: p.grades }),
    onSuccess: (data) => {
      qc.setQueryData(["gpa-subject-config"], data);
      void qc.invalidateQueries({ queryKey: ["gpa-config"] });
      void qc.invalidateQueries({ queryKey: ["gpa-grades"] });
      void qc.invalidateQueries({ queryKey: ["grade-items"] });
    },
  });

  if (cfg.isLoading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (!current) return <p className="text-sm text-slate-500">{t("gpa.subjects.noSubjects")}</p>;
  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white" data-testid="gpa-subjects">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-700">{t("gpa.subjects.title")}</h2>
        <p className="text-xs text-slate-400">{t("gpa.subjects.hint")}</p>
      </div>
      <div className="flex flex-col md:flex-row">
        <nav className="flex gap-1 overflow-x-auto border-b border-slate-100 p-2 md:w-48 md:shrink-0 md:flex-col md:border-b-0 md:border-r" aria-label={t("gpa.subjects.title")}>
          {list.map((s) => (
            <button key={s.slug} type="button" onClick={() => setSlug(s.slug)}
              className={`flex items-center justify-between gap-2 whitespace-nowrap rounded px-3 py-1.5 text-left text-sm ${s.slug === current.slug ? "bg-sky-50 font-medium text-sky-700" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid={`gpa-subject-${s.slug}`}>
              <span className="truncate">{s.name}</span>
              {!s.scheme && <span className="text-[10px] text-amber-600">{t("gpa.subjects.unset")}</span>}
            </button>
          ))}
        </nav>
        <SubjectEditor key={current.slug} subject={current} saving={save.isPending} error={save.error ? (save.error as Error).message : null}
          onSave={(items, grades) => save.mutate({ slug: current.slug, items, grades })} saved={save.isSuccess} />
      </div>
    </section>
  );
}

function SubjectEditor({ subject, onSave, saving, error, saved }: { subject: SubjectCfg; onSave: (items: Item[], grades: Record<string, GradeRule>) => void; saving: boolean; error: string | null; saved: boolean }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>(subject.scheme?.items ?? []);
  const [grades, setGrades] = useState<Record<string, GradeRule>>(subject.grades);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setItems(subject.scheme?.items ?? []); setGrades(subject.grades); setDirty(false); }, [subject]);
  const total = items.reduce((a, b) => a + (Number(b.weight) || 0), 0);
  const upd = (i: number, p: Partial<Item>) => { setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...p } : x))); setDirty(true); };
  const updGrade = (g: string, p: Partial<GradeRule>) => {
    setGrades((gs) => ({ ...gs, [g]: { credits: gs[g]?.credits ?? null, gpa_enabled: gs[g]?.gpa_enabled ?? true, graded: gs[g]?.graded ?? true, ...p } }));
    setDirty(true);
  };
  // 等级制 / 合格制:至少两级,每级有名称和折算分(0–100),名称不重复
  const levelsOk = (i: Item) => {
    if (itemScale(i) === "percent") return true;
    const lv = i.levels ?? [];
    const labels = lv.map((x) => x.label.trim());
    return lv.length >= 2 && labels.every(Boolean) && new Set(labels).size === labels.length && lv.every((x) => Number.isFinite(x.percent) && x.percent >= 0 && x.percent <= 100);
  };
  const valid = items.length > 0 && Math.abs(total - 100) < 0.01 && items.every((i) => i.label.trim()) && items.every(levelsOk);
  const setScale = (i: number, scale: ScaleType) => upd(i, scale === "percent" ? { scale: undefined, levels: undefined } : { scale, levels: DEFAULT_LEVELS[scale].map((x) => ({ ...x })) });
  const updLevel = (i: number, n: number, p: Partial<ScaleLevel>) => upd(i, { levels: (items[i]?.levels ?? []).map((x, m) => (m === n ? { ...x, ...p } : x)) });

  return (
    <div className="min-w-0 flex-1 p-4 text-sm">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-800">{subject.name}</h3>
        {!subject.scheme && items.length === 0 && (
          <button type="button" onClick={() => { setItems(TEMPLATE.map((x) => ({ ...x }))); setDirty(true); }} className="rounded border border-sky-300 px-3 py-1 text-xs text-sky-700 hover:bg-sky-50" data-testid="gpa-subject-template">
            {t("gpa.subjects.useTemplate")}
          </button>
        )}
      </div>

      <h4 className="mb-1 text-xs font-medium text-slate-500">{t("gpa.subjects.items")}</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-400">
            <tr><th className="py-1 pr-2 text-left font-normal">{t("gpa.subjects.itemName")}</th><th className="py-1 pr-2 text-left font-normal">{t("gpa.subjects.category")}</th><th className="py-1 pr-2 text-left font-normal">{t("gpa.subjects.scale")}</th><th className="w-24 py-1 pr-2 text-right font-normal">{t("gpa.subjects.weight")}</th><th className="w-8" /></tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <Fragment key={i}>
              <tr className="border-t border-slate-100">
                <td className="py-1 pr-2"><input id={`item-label-${i}`} value={it.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder={t("gpa.subjects.itemPh")} className="w-full rounded border border-slate-200 px-2 py-1" /></td>
                <td className="py-1 pr-2">
                  <select id={`item-cat-${i}`} value={it.category} onChange={(e) => upd(i, { category: e.target.value as Category })} className={`rounded border px-2 py-1 text-xs ${CAT_CLS[it.category]}`}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{t(`gpa.subjects.cat.${c}`)}</option>)}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  {/* 计分方式(F-0):百分制是旧行为;等级 / 合格制在下一行配等级 → 分数映射 */}
                  <select id={`item-scale-${i}`} value={itemScale(it)} onChange={(e) => setScale(i, e.target.value as ScaleType)} className="rounded border border-slate-200 px-2 py-1 text-xs" data-testid={`gpa-item-scale-${i}`}>
                    {SCALE_TYPES.map((sc) => <option key={sc} value={sc}>{t(`gpa.subjects.scaleOf.${sc}`)}</option>)}
                  </select>
                </td>
                <td className="py-1 pr-2 text-right"><input id={`item-w-${i}`} type="number" value={it.weight} onChange={(e) => upd(i, { weight: Number(e.target.value) })} className="w-16 rounded border border-slate-200 px-2 py-1 text-right tabular-nums" /> %</td>
                <td className="py-1 text-right"><button type="button" aria-label={t("common.delete")} onClick={() => { setItems((xs) => xs.filter((_, j) => j !== i)); setDirty(true); }} className="text-rose-500">×</button></td>
              </tr>
              {itemScale(it) !== "percent" && (
                <tr className="bg-slate-50/60">
                  <td colSpan={5} className="px-2 pb-2 pt-1">
                    <div className="mb-1 text-[11px] text-slate-500">{t("gpa.subjects.levels")} · {t("gpa.subjects.levelsHint")}</div>
                    <div className="flex flex-wrap items-center gap-2">
                      {itemLevels(it).map((lv, n) => (
                        <span key={n} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-xs">
                          <input aria-label={`${it.label} ${t("gpa.subjects.levelLabel")} ${n + 1}`} value={lv.label} onChange={(e) => updLevel(i, n, { label: e.target.value })} className="w-16 rounded border border-slate-200 px-1 py-0.5" />
                          <span className="text-slate-400">=</span>
                          <input aria-label={`${it.label} ${lv.label} ${t("gpa.subjects.levelPercent")}`} type="number" min={0} max={100} value={lv.percent} onChange={(e) => updLevel(i, n, { percent: Number(e.target.value) })} className="w-14 rounded border border-slate-200 px-1 py-0.5 text-right tabular-nums" />
                          <button type="button" aria-label={t("common.delete")} onClick={() => upd(i, { levels: itemLevels(it).filter((_, m) => m !== n) })} className="text-rose-400">×</button>
                        </span>
                      ))}
                      <button type="button" onClick={() => upd(i, { levels: [...itemLevels(it), { label: "", percent: 60 }] })} className="text-xs text-sky-600 hover:underline">+ {t("gpa.subjects.addLevel")}</button>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs">
        <button type="button" onClick={() => { setItems((xs) => [...xs, { key: "", label: "", weight: 0, category: "exam" }]); setDirty(true); }} className="text-sky-600 hover:underline" data-testid="gpa-subject-add-item">+ {t("gpa.config.addItem")}</button>
        <span className={Math.abs(total - 100) < 0.01 ? "text-emerald-600" : "text-rose-600"}>{t("gpa.config.total", { n: total })}</span>
      </div>
      <p className="mt-2 text-xs text-slate-400">{t("gpa.subjects.catHint")}</p>

      <h4 className="mb-1 mt-4 text-xs font-medium text-slate-500">{t("gpa.subjects.credits")}</h4>
      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead className="text-xs text-slate-400"><tr><th className="py-1 pr-3 text-left font-normal">{t("gpa.subjects.grade")}</th><th className="py-1 pr-3 text-left font-normal">{t("gpa.config.credit")}</th><th className="py-1 pr-3 text-left font-normal">{t("gpa.grades.settings.gpaEnabled")}</th><th className="py-1 text-left font-normal">{t("gpa.grades.settings.graded")}</th></tr></thead>
          <tbody>
            {GRADES.map((g) => {
              const r = grades[g];
              return (
                <tr key={g} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-slate-600">{g}</td>
                  <td className="py-1 pr-3"><input id={`credit-${g}`} type="number" step="0.1" value={r?.credits ?? ""} placeholder="—" onChange={(e) => updGrade(g, { credits: e.target.value === "" ? null : Number(e.target.value) })} className="w-20 rounded border border-slate-200 px-2 py-1 text-right tabular-nums" /></td>
                  <td className="py-1 pr-3"><input type="checkbox" aria-label={`${g} ${t("gpa.grades.settings.gpaEnabled")}`} checked={r?.gpa_enabled ?? true} onChange={(e) => updGrade(g, { gpa_enabled: e.target.checked })} /></td>
                  <td className="py-1"><input type="checkbox" aria-label={`${g} ${t("gpa.grades.settings.graded")}`} checked={r?.graded ?? true} onChange={(e) => updGrade(g, { graded: e.target.checked })} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {subject.course_overrides.length > 0 && (
        <p className="mt-3 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {t("gpa.subjects.overrides", { list: subject.course_overrides.map((o) => `${o.course}(${o.scheme_name ?? "—"})`).join("、") })}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button type="button" disabled={!valid || saving || !dirty} onClick={() => onSave(items, Object.fromEntries(Object.entries(grades).filter(([, v]) => v)))}
          className="rounded bg-sky-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-600 disabled:opacity-50" data-testid="gpa-subject-save">
          {saving ? t("common.saving") : t("common.save")}
        </button>
        {!valid && items.length > 0 && <span className="text-xs text-rose-600">{items.every(levelsOk) ? t("gpa.subjects.invalid") : t("gpa.subjects.invalidLevels")}</span>}
        {saved && !dirty && <span className="text-xs text-emerald-700">{t("gpa.subjects.saved")}</span>}
        {error && <span className="text-xs text-rose-600">{error}</span>}
      </div>
    </div>
  );
}
